/**
 * Quick contact action shown on Live Activity rows.
 * Three actions: Call (tel:), SMS (sms:), in-app Message.
 * Admin / Manager / Office can chase a worker mid-trip without leaving the dashboard.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquare, Send, X } from "lucide-react";
import { Button, Modal, Textarea, Input } from "../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_TEMPLATES = [
  "All good mate? Where are you up to?",
  "Need an ETA mate.",
  "Can you give me a call when you get a sec?",
  "Don't forget to clock out when you finish up.",
  "Job's been pushed back — hold off on heading there.",
];

export const QuickContactModal = ({ isOpen, onClose, target }) => {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  if (!isOpen || !target) return null;

  const phone = target.user_phone;
  const sanitisedPhone = phone ? phone.replace(/[^+\d]/g, "") : "";

  const sendMessage = async () => {
    if (!content.trim()) {
      toast.error("Type a message first, mate");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: target.user_id,
        subject: subject || "Quick check-in",
        content,
        message_type: "general",
      });
      toast.success(`Message sent to ${target.user_name}`);
      setSubject("");
      setContent("");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Contact ${target.user_name}`}
      size="md"
    >
      <div className="space-y-4" data-testid="quick-contact-modal">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <p className="font-bold">{target.user_name}</p>
          {target.user_role && (
            <p className="text-xs text-slate-500 capitalize">{target.user_role.replace("_", " ")}</p>
          )}
          {phone ? (
            <p className="text-xs text-slate-600 mt-1">📱 {phone}</p>
          ) : (
            <p className="text-xs text-amber-600 mt-1">No phone number on file — only in-app message available.</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a
            href={sanitisedPhone ? `tel:${sanitisedPhone}` : undefined}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold uppercase text-sm transition-colors ${
              sanitisedPhone
                ? "bg-green-500 hover:bg-green-600 text-white"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
            onClick={(e) => { if (!sanitisedPhone) e.preventDefault(); }}
            data-testid="quick-contact-call"
          >
            <Phone className="w-4 h-4" /> Call
          </a>
          <a
            href={sanitisedPhone ? `sms:${sanitisedPhone}` : undefined}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold uppercase text-sm transition-colors ${
              sanitisedPhone
                ? "bg-blue-500 hover:bg-blue-600 text-white"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
            onClick={(e) => { if (!sanitisedPhone) e.preventDefault(); }}
            data-testid="quick-contact-sms"
          >
            <MessageSquare className="w-4 h-4" /> SMS
          </a>
        </div>

        <div className="border-t pt-4 space-y-3">
          <p className="text-xs font-bold uppercase text-slate-600 flex items-center gap-2">
            <Send className="w-3.5 h-3.5" /> In-app message
          </p>
          <div className="flex flex-wrap gap-1">
            {QUICK_TEMPLATES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setContent(t)}
                className="text-[11px] px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                data-testid={`quick-template-${t.slice(0, 12)}`}
              >
                {t.slice(0, 28)}{t.length > 28 ? "…" : ""}
              </button>
            ))}
          </div>
          <Input
            label="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Quick check-in"
            testId="quick-contact-subject"
          />
          <Textarea
            label="Message"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Drop them a line..."
            rows={3}
            testId="quick-contact-message"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
            <Button onClick={sendMessage} disabled={sending} data-testid="quick-contact-send">
              <Send className="w-4 h-4 mr-1" /> {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default QuickContactModal;
